import { Badge } from "shared/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { AlertTriangle, CheckCircle2, ClipboardList } from "lucide-react"

type DocumentStatus = {
  id: string
  name: string
  code: string
  isMandatory: boolean
  uploaded: boolean
  files: { id: string; name: string; url?: string | null }[]
}

interface DocumentsTabProps {
  documentsWithStatus: DocumentStatus[]
  uploadedCount: number
  totalDocuments: number
  missingMandatory: number
}

export function DocumentsTab({ documentsWithStatus, uploadedCount, totalDocuments, missingMandatory }: DocumentsTabProps) {
  return (
    <div className="space-y-4 pt-0">
      <section className="rounded-lg border border-dashed border-gray-300 p-4 text-sm">
        <p className="font-semibold text-gray-900">สถานะเอกสารแนบ</p>
        <p className="text-xs text-gray-500">
          {uploadedCount} / {totalDocuments || 0} เอกสารถูกแนบ
          {missingMandatory > 0 ? (
            <span className="ml-2 text-red-600">(ขาด {missingMandatory} รายการที่จำเป็น)</span>
          ) : null}
        </p>
      </section>

      {documentsWithStatus.length === 0 ? (
        <p className="text-xs text-gray-500">ยังไม่พบรายการเอกสารที่ต้องแนบ</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="text-xs uppercase tracking-wide text-gray-500">Document</TableHead>
              <TableHead className="text-right text-xs uppercase tracking-wide text-gray-500">Mandatory</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documentsWithStatus.map((doc) => (
              <TableRow key={doc.id}>
                <TableCell className="w-10">
                  {doc.uploaded ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : doc.isMandatory ? (
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                  ) : (
                    <ClipboardList className="h-4 w-4 text-gray-400" />
                  )}
                </TableCell>
                <TableCell>
                  <div className="text-sm font-medium text-gray-800">{doc.name || "เอกสารแนบ"}</div>
                  {doc.files.length > 0 ? (
                    <ul className="mt-2 space-y-1 pl-4">
                      {doc.files.map((file) => (
                        <li key={file.id} className="break-words text-[11px] text-gray-500">
                          {file.url ? (
                            <a
                              href={file.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline"
                            >
                              {file.name}
                            </a>
                          ) : (
                            file.name
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </TableCell>
                <TableCell className="text-right">
                  {doc.isMandatory ? (
                    <Badge variant={doc.uploaded ? "secondary" : "outline"} className="text-[11px]">
                      Required
                    </Badge>
                  ) : (
                    <span className="text-xs text-gray-500">Optional</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}
